/**
 * The page-guide data contract.
 *
 * Pure data and pure functions — no React, no Fluent UI, no router, no i18next. The Node scripts
 * (`scripts/help-check.mjs`, `scripts/build-user-manual.mjs`) and the registry test import this
 * file and the registry directly, so a UI import sneaking in here breaks all of them.
 */

/**
 * The role ladder, mirroring how the UI gates controls (`hooks/useRoleType.tsx`):
 *
 * | `HelpRole`   | Who                                                             | `useRoleType` flag     |
 * |--------------|-----------------------------------------------------------------|------------------------|
 * | `user`       | anyone signed in, read-only roles included                      | —                      |
 * | `hr`         | may edit employees and enrollments on the HR pages              | `!hasHrReadOnly`       |
 * | `admin`      | may edit clients, benefits and packages (Company/Country/Global) | `hasAdminRole`         |
 * | `superadmin` | SuperAdmin only (photos, hard deletes, …)                        | `hasSuperAdminRole`    |
 *
 * Linear because each tier is a superset of the one below: every admin role can edit HR data,
 * and SuperAdmin is an admin role. Which PAGES a role sees is decided by the router
 * (`PAGES_PER_ROLES`), not here — a guide only exists on a page the user can already open.
 */
export type HelpRole = "user" | "hr" | "admin" | "superadmin";

/**
 * The languages a guide may be written in, EN being the one it is always written in.
 *
 * The guide follows the language picked in the app's own settings menu (see `helpLanguageFor`);
 * a language listed here gets its translations shown, any other falls back to EN. Add `"de"`
 * here before adding `i18n: { de: … }` blocks to a guide — `registry.test.ts` rejects
 * translations under a language that is not listed.
 */
export const HELP_LANGUAGES = ["en"] as const;
export type HelpLanguage = (typeof HELP_LANGUAGES)[number];
/** EN is the source text, never a translation — hence `i18n` covers only the others. */
export const HELP_BASE_LANGUAGE: HelpLanguage = "en";
export type HelpTranslatedLanguage = Exclude<HelpLanguage, "en">;
/**
 * The key of an `i18n` block. While EN is the only language, `HelpTranslatedLanguage` is `never`
 * and every lookup would type as `never`; widened to `string` then (the registry test still
 * rejects a translation under a language that is not listed).
 */
type I18nKey = [HelpTranslatedLanguage] extends [never] ? string : HelpTranslatedLanguage;

/** The app's i18next language (`de-AT`, `sr-Latn-RS`, `en-GB`) → the guide language to show. */
export const helpLanguageFor = (appLanguage: string | undefined): HelpLanguage => {
  const prefix = (appLanguage ?? "").toLowerCase().split("-")[0];
  return HELP_LANGUAGES.find((code) => code === prefix) ?? HELP_BASE_LANGUAGE;
};

/**
 * Translations, per field and optional at every level.
 *
 * Per FIELD rather than per step, because a half-translated guide is the normal state of one.
 * Anything unset falls back to the EN text, and the surface says so rather than passing the
 * fallback off as a translation.
 *
 * Selectors are NOT translatable: the anchor is the contract between guide and page, shared
 * across languages — which is why `help:check`, `help:impact` and the hooks are language-blind.
 */
export type HelpStepI18n = Partial<Record<I18nKey, { title?: string; body?: string }>>;

export type PageHelpI18n = Partial<
  Record<
    I18nKey,
    {
      title?: string;
      intro?: string;
      /** EN section name → translated heading. Keyed by the EN name, which stays the id. */
      sections?: Record<string, string>;
      /** `HelpStep.context` key → the translated "how to get there" line. */
      contexts?: Record<string, string>;
      tips?: string[];
    }
  >
>;

/**
 * What the signed-in user is allowed to do — the `useRoleType()` flags, passed in as plain
 * booleans so `help/` stays importable with no providers (a test or a Node script has no auth).
 */
export interface HelpViewer {
  /** May edit employees and enrollments — `hr` and above (`!hasHrReadOnly`). */
  canEditHr: boolean;
  /** An admin role (`hasAdminRole`) — `admin` and above. */
  isAdmin: boolean;
  /** `hasSuperAdminRole`. */
  isSuperAdmin: boolean;
}

export interface HelpStep {
  /**
   * Always a `[data-help="…"]` attribute selector, never a class or text match.
   * The attribute is the contract between the guide and the page; classes and labels
   * change on every restyle.
   */
  selector: string;
  /** Short action phrase — "Filter the list by status", not "Filter chips". */
  title: string;
  /** 1–4 plain sentences: what it does, when to use it, what happens next. */
  body: string;
  /**
   * Groups the card in the drawer. Order comes from `PageHelp.sections`; a step with an
   * unlisted (or missing) section falls into a trailing "More" group.
   */
  section?: string;
  /** Hide the step from lower roles — ONLY when the control itself is hidden from them. */
  minRole?: HelpRole;
  /**
   * The element only exists after a UI action — a key in `PageHelp.contexts`, whose value
   * tells the reader how to get there ("Click Add employee…"). The tour skips these while
   * they are off screen; the drawer still lists them, with the instruction attached.
   */
  context?: string;
  /** The element needs data to exist (a non-empty list, a filled row). */
  optional?: boolean;
  /** Translations of `title` / `body`. EN is this object's own fields — see `HelpStepI18n`. */
  i18n?: HelpStepI18n;
}

export interface PageHelp {
  title: string;
  /** 1–3 sentences: what the page is for and where it sits in the workflow. */
  intro: string;
  /** Display order for `HelpStep.section`. Sections not listed here render last. */
  sections?: string[];
  /** `HelpStep.context` key → how the reader reveals that part of the UI. */
  contexts?: Record<string, string>;
  /** Cross-cutting notes that belong to no single control. */
  tips?: string[];
  steps: HelpStep[];
  /** Translations of the page-level prose. Steps carry their own — see `PageHelpI18n`. */
  i18n?: PageHelpI18n;
}

/**
 * The prose to show, in the reader's language, with EN standing in per field where nothing
 * has been written yet. `translated` is false when ANY displayed field fell back, so a card can
 * say "EN" instead of quietly mixing the two languages.
 */
export interface LocalizedText<T> {
  value: T;
  translated: boolean;
}

const pick = <T>(translation: T | undefined, base: T): LocalizedText<T> =>
  translation === undefined || translation === null || translation === ""
    ? { value: base, translated: false }
    : { value: translation, translated: true };

export const localizedStep = (
  step: HelpStep,
  language: HelpLanguage
): { title: string; body: string; translated: boolean } => {
  if (language === HELP_BASE_LANGUAGE) {
    return { title: step.title, body: step.body, translated: true };
  }
  const source = step.i18n?.[language as I18nKey];
  const title = pick(source?.title, step.title);
  const body = pick(source?.body, step.body);
  return {
    title: title.value,
    body: body.value,
    translated: title.translated && body.translated,
  };
};

export const localizedPage = (
  help: PageHelp,
  language: HelpLanguage
): {
  title: string;
  intro: string;
  tips: string[] | undefined;
  translated: boolean;
} => {
  if (language === HELP_BASE_LANGUAGE) {
    return { title: help.title, intro: help.intro, tips: help.tips, translated: true };
  }
  const source = help.i18n?.[language as I18nKey];
  const title = pick(source?.title, help.title);
  const intro = pick(source?.intro, help.intro);
  const tips = pick(source?.tips?.length ? source.tips : undefined, help.tips);
  return {
    title: title.value,
    intro: intro.value,
    tips: tips.value,
    translated:
      title.translated && intro.translated && (!help.tips?.length || tips.translated),
  };
};

/** A section heading is an id in EN and a label in every language, hence keyed by the EN name. */
export const localizedSection = (
  help: PageHelp,
  section: string,
  language: HelpLanguage
): string =>
  language === HELP_BASE_LANGUAGE
    ? section
    : help.i18n?.[language as I18nKey]?.sections?.[section] ?? section;

export const localizedContext = (
  help: PageHelp,
  key: string,
  language: HelpLanguage
): string | undefined =>
  (language === HELP_BASE_LANGUAGE
    ? undefined
    : help.i18n?.[language as I18nKey]?.contexts?.[key]) ??
  help.contexts?.[key];

/** The whole `contexts` map in one language, for a surface that is handed the record itself. */
export const localizedContexts = (
  help: PageHelp,
  language: HelpLanguage
): Record<string, string> | undefined => {
  if (!help.contexts) return undefined;
  if (language === HELP_BASE_LANGUAGE) return help.contexts;
  const translated = help.i18n?.[language as I18nKey]?.contexts;
  if (!translated) return help.contexts;
  return Object.fromEntries(
    Object.entries(help.contexts).map(([key, value]) => [key, translated[key] ?? value])
  );
};

export const ROLE_RANK: Record<HelpRole, number> = {
  user: 0,
  hr: 1,
  admin: 2,
  superadmin: 3,
};

export const rankForViewer = (viewer: HelpViewer): number =>
  viewer.isSuperAdmin ? 3 : viewer.isAdmin ? 2 : viewer.canEditHr ? 1 : 0;

export const stepVisibleForRole = (step: HelpStep, viewer: HelpViewer): boolean =>
  rankForViewer(viewer) >= ROLE_RANK[step.minRole ?? "user"];

/** The role badge shown on a card, or `null` when the step is visible to everyone. */
export const roleBadgeLabel = (step: HelpStep): string | null => {
  switch (step.minRole) {
    case "hr":
      return "HR editor+";
    case "admin":
      return "Admin";
    case "superadmin":
      return "Superadmin";
    default:
      return null;
  }
};

/**
 * Steps grouped for the drawer, in `PageHelp.sections` order. Sections present on steps but
 * missing from that list keep their first-seen order and land after the declared ones, so
 * adding a step without touching `sections` degrades to "shown last" rather than "dropped".
 *
 * Takes the section list rather than the whole `PageHelp` because the tour groups too — and
 * the tour only ever holds the on-screen subset of the steps.
 */
export const groupStepsBySection = (
  sections: string[] | undefined,
  steps: HelpStep[]
): { section: string; steps: HelpStep[] }[] => {
  const FALLBACK = "More";
  const buckets = new Map<string, HelpStep[]>();
  for (const section of sections ?? []) buckets.set(section, []);
  for (const step of steps) {
    const key = step.section ?? FALLBACK;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(step);
    else buckets.set(key, [step]);
  }
  return Array.from(buckets.entries())
    .filter(([, group]) => group.length > 0)
    .map(([section, group]) => ({ section, steps: group }));
};

/**
 * The anchor name behind a step's selector — `[data-help="hr-add-employee"]` →
 * `hr-add-employee`. What "Point at a field" runs on: it starts from the element the user
 * pointed at and has to find the step that documents it. Returns null for anything that is not
 * the plain attribute selector (`registry.test.ts` enforces the shape).
 */
export const anchorNameOf = (selector: string): string | null =>
  /^\[data-help="([^"]+)"\]$/.exec(selector)?.[1] ?? null;

/**
 * Free-text match for the drawer's search box and the tour's jump list.
 *
 * Every whitespace-separated term must appear somewhere in the step, in any order — bodies
 * included, because the user who cannot name the field is the one searching — and in every
 * language at once, so an untranslated step does not vanish when another language is selected.
 * The selector is NOT searched: "help", "data" or a page prefix like "hr" would match every step.
 */
export const stepMatchesQuery = (step: HelpStep, query: string): boolean => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const translations = Object.values(step.i18n ?? {})
    .map((t) => `${t?.title ?? ""} ${t?.body ?? ""}`)
    .join(" ");
  const haystack =
    `${step.title} ${step.body} ${step.section ?? ""} ${translations}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
};
